import React, { useEffect, useState } from 'react';
import {
  ProviderConsumer,
  Flex,
  Input,
  SearchIcon,
  Card,
  Text,
  CardHeader,
  Alert,
  InfoIcon
} from '@fluentui/react-northstar';
import { TEMPLATE } from '../shared/utils/constants';
import { TicketTemplate, Ticket } from '../shared/interfaces/ticket.interface';
import TemplateFieldsCardBody from './TemplateFieldsCardBody';
import { TemplateFormStateProps } from './TemplateFieldsForm';
import { mappingTemplateProps } from './SetDefault_TemplateForm';
import { EmptyData } from '../shared/components/EmptyData';
import EmptySVG from '../../svg/empty.svg';
import { filterListBySearch, getThemeMode } from '../shared/utils/helper';
import CustomFormIcon from '../../svg/custom-form.svg';
import useWindowWidth from '../tikit/useWindowWidth';

export interface TicketTemplatesSelectionProperties {
  ticketData: Ticket;
  formData: TemplateFormStateProps;
  dispatchEvent?: any;
  dataSourceList?: any[];
  affectedUserList?: any[];
  collaboratorList?: any[];
}

export const TicketTemplatesSelection = (
  props: TicketTemplatesSelectionProperties
) => {
  const { formData, ticketData, dispatchEvent } = props;

  const [templates, setTemplates] = useState(
    Object.assign([], formData.templates)
  );
  const [template, setTemplate] = useState<TicketTemplate>(
    ticketData['SelectedTemplate'] || ({} as TicketTemplate)
  );

  const isMobile = useWindowWidth() <= 767;

  useEffect(() => {
    setTemplates(currentItems => {
      return currentItems.map((templateData, index) => {
        templateData['hidden'] = false;
        return mappingTemplateProps(templateData, formData);
      });
    });
  }, []);

  useEffect(() => {
    if(ticketData["TemplateHasError"]){
      const divElement = document.getElementById("templateSelectionContent");
      if(divElement)
        divElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
    if(ticketData['IsTemplateSelectionSaving'])
      dispatchEvent({type:"IsTemplateSelectionSaving", data:false});
  }, [ticketData["TemplateHasError"], ticketData['IsTemplateSelectionSaving']])

  useEffect(() => {
    if (template?.Id) {
      const selectedElement = document.getElementById(`template-card-${template.Id}`);
      if (selectedElement)
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });
    }
  }, [template]);

  const handleSearch = (event: any) => {
    const target = event.target.value.toString() || '';
    setTemplates(() => filterListBySearch(templates, target));
  };

  const onSelectedCard = (checked: boolean, item: TicketTemplate) => {
    if (!checked) {
      ticketData['SelectedTemplate'] = {};
      setTemplate({} as TicketTemplate);
      dispatchEvent({
        type: 'set-selected-template',
        data: {
          SelectedTemplate: {}
        }
      });
    } else {
      ticketData['SelectedTemplate'] = item;
      setTemplate(item);
      dispatchEvent({
        type: 'set-selected-template',
        data: {
          SelectedTemplate: item
        }
      });
    }
  };

  const renderItems = templates.map((item: TicketTemplate, index) => {
    return (
      <Card
        fluid
        id={`template-card-${item.Id}`}
        className={`mt-4`}
        elevated
        {...(template.Id && template.Id == item.Id && { selected: true })}
        hidden={item['hidden']}
        onClick={() => {
          onSelectedCard(template.Id != item.Id, item);
        }}
      >
        <Card.TopControls style={{ zIndex: 1 }}>
          {!item.NoCustomForm && <CustomFormIcon className={"mr-4"} />}
        </Card.TopControls>
        <CardHeader>
          <Flex gap="gap.small">
            <Flex column styles={{ maxWidth: '90%' }}>
              <Text content={item.Name} weight="semibold" />
            </Flex>
          </Flex>
        </CardHeader>
        <TemplateFieldsCardBody templateData={item} formData={formData} />
      </Card>
    );
  });

  const mobileHeaderStyle = (globalTheme) => isMobile ? 
    {
      top: "0",
      paddingTop:"40px",
      paddingBottom: "40px",
      backgroundColor: getThemeMode() === "dark" ? globalTheme.siteVariables.colorScheme.default.backgroundHover3 : globalTheme.siteVariables.colorScheme.default.background
    } : {backgroundColor: getThemeMode() === "dark" ? globalTheme.siteVariables.colorScheme.default.backgroundHover3 : globalTheme.siteVariables.colorScheme.default.background};

  return (
    <ProviderConsumer
      render={globalTheme => (
        <>
          <Flex
            column
            gap="gap.large"
          >
            <Flex.Item>
              <Flex
                column
                gap="gap.small"
                className='fixed md:relative z-10 w-screen md:w-auto left-0 px-6 md:px-0 pb-3 md:pb-0'
                style={mobileHeaderStyle(globalTheme)}
              >
                <Flex.Item>
                  <Flex column>
                    <div id="templateSelectionContent">
                      <Text
                        content={TEMPLATE.SELECT_TEMPLATE}
                        weight="bold"
                        size="large"
                      />
                    </div>
                    <Text content={TEMPLATE.SELECT_TEMPLATE_DESCRIPTION} />
                    {ticketData["TemplateHasError"] && (
                      <Alert className="mt-2" danger icon={<InfoIcon />} content={
                          <>
                          <Text content={ticketData["TemplateErrorMessage"]} />
                        </>
                      } />
                    )}
                  </Flex>
                  
                </Flex.Item>
                <Flex.Item>
                  <Flex vAlign="center">
                    <Text content={TEMPLATE.EXISTING_TEMPLATES} weight="bold" style={{margin: '1vh'}} />
                    <Flex.Item push>
                      <Input
                        icon={<SearchIcon />}
                        placeholder={TEMPLATE.TEMPLATE_INPUT_PLACEHOLDER}
                        autoFocus={true}
                        onChange={handleSearch}
                        input={getThemeMode() === "light" ? {style:{backgroundColor: "rgb(245, 245, 245)"}} : {}}
                      />
                    </Flex.Item>
                  </Flex>
                </Flex.Item>
              </Flex>
            </Flex.Item>
            <Flex.Item>
              <div className='h-32 md:hidden'>

              </div>
            </Flex.Item>
            <Flex.Item>
              <div className="h-auto md:max-h-50vh overflow-y-hidden md:overflow-y-auto">
                <div className={`grid grid-cols-1 gap-4 lg:grid-cols-2 scrollableContainer`}>
                  {renderItems?.length > 0 ? (
                    renderItems
                  ) : (
                    <EmptyData
                      headerText={TEMPLATE.EMPTY_TEMPLATE_SELECTION}
                      subheaderText={''}
                      SVGIcon={<EmptySVG width={130} height={130} />}
                    />
                  )}
                </div>
              </div>
            </Flex.Item>
            
            <Flex.Item>
              <div className='h-20 md:hidden'>

              </div>
            </Flex.Item>
          </Flex>
        </>
      )}
    />
  );
};
